import React, { useEffect, useRef, useState } from 'react'
import * as echarts from 'echarts'
import { useAppDispatch } from '../../../../app/hooks'
import { setProrithmEcg } from './DeviceSlice'
import { Box } from '@mui/material'
import { backgroundImage } from './Constant'

interface ECGData {
  e: number // ECG value (Y-axis)
  t: number // Timestamp (X-axis)
  o: number // Order
}

const ECGChart = ({ ecgData }: { ecgData: ECGData[] }) => {
  const dispatch = useAppDispatch()
  const chartRef = useRef<HTMLDivElement>(null)
  const [chartInstance, setChartInstance] = useState<echarts.ECharts | null>(
    null
  )
  const [data, setData] = useState<{ x: number; y: number }[]>([])
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  const indexRef = useRef(0) // Tracks the current index in ecgData
  const maxDataPoints = 700 // Window size for visible points

  // Initialize ECharts
  useEffect(() => {
    if (chartRef.current) {
      const instance = echarts.init(chartRef.current)
      setChartInstance(instance)

      // Resize chart when window size changes
      const handleResize = () => instance.resize()
      window.addEventListener('resize', handleResize)

      return () => {
        window.removeEventListener('resize', handleResize)
        instance.dispose()
        dispatch(setProrithmEcg([]))
      }
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    // if (!chartInstance || ecgData?.length === 0) return
    // indexRef.current = 0 // Reset index when new data arrives

    const option: echarts.EChartsOption = {
      graphic: {
        type: 'image',
        left: 0,
        top: 0,
        z: -10,
        style: {
          image: backgroundImage,
          width: chartInstance?.getWidth?.() || 800,
          height: chartInstance?.getHeight?.() || 230,
          opacity: 0.25,
        },
      },
      backgroundColor: '#000000',
      animation: true,
      title: {
        text: 'ECG Graph',
        left: 'center',
        textStyle: { fontSize: 16, color: '#ffffff' },
      },
      legend: {
        data: ['ECG Signal'],
        bottom: 0,
        textStyle: {
          fontSize: 12,
          color: '#ffffff',
        },
        selectedMode: false,
      },
      // tooltip: {
      //   trigger: 'axis',
      //   axisPointer: {
      //     type: 'line', // Use a line pointer
      //   },
      //   formatter: (params: any) => {
      //     if (Array.isArray(params) && params.length > 0) {
      //       const point = params[0]
      //       return `<strong>ECG Signal</strong><br/>Time: ${point.name}<br/>Value: ${point.value}`
      //     }
      //     return ''
      //   },
      // },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        show: true,
        data: data?.map((d) => d?.x),
        axisLabel: { fontSize: 12, show: false },
        axisLine: { show: false },
        splitLine: {
          show: false,
          lineStyle: {
            color: '#c0c0c0', // Light grid lines
            type: 'solid',
            width: 1, // Adjust for thickness
          },
        },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        min: Math.min(...ecgData.map((d) => d?.e)) - 50,
        max: Math.max(...ecgData.map((d) => d?.e)) + 100,
        axisLabel: {
          fontSize: 12,
          formatter: (value: number) => value.toFixed(0),
          show: false,
        },
        splitLine: {
          show: false,
          lineStyle: {
            color: '#c0c0c0',
            type: 'solid',
            width: 1,
          },
        },
        axisTick: { show: false },
      },
      series: [
        {
          symbol: 'triangle',
          name: 'ECG Signal',
          type: 'line',
          data: data?.map((d) => d?.y),
          smooth: true,
          showSymbol: false,
          lineStyle: {
            color: '#00FF00',
            width: 2,
          },
          z: 10,
        },
      ],
      grid: {
        left: '1%',
        right: '1%',
        bottom: '12%',
        top: '12%',
        containLabel: true,
      },
      // dataZoom: [
      //   {
      //     type: 'inside', // Enables zooming with mouse scroll or pinch
      //     xAxisIndex: [0], // Applies zoom to x-axis
      //   },
      //   {
      //     type: 'slider', // Adds a zoom slider below the chart
      //     xAxisIndex: [0],
      //     start: 0, // Default view from 0% of data
      //     end: 100, // Default view to 100% of data
      //   },
      // ],
    }

    chartInstance?.setOption(option)

    if (!chartInstance || ecgData?.length === 0) return

    intervalRef.current = setInterval(() => {
      setData((prevData) => {
        if (indexRef.current >= ecgData.length) {
          clearInterval(intervalRef.current ?? undefined) // Stop plotting once all data is plotted
          intervalRef.current = null
          return prevData
        }

        const newPoint = {
          x: ecgData?.[indexRef.current]?.t, // Timestamp from dataset
          y: ecgData?.[indexRef.current]?.e, // ECG value from dataset
        }

        indexRef.current += 1 // Move to next data point

        const newData = [...prevData, newPoint].slice(-maxDataPoints) // Maintain fixed size window

        // Recalculate min/max based on new data
        const newMin = Math.min(...newData.map((d) => d.y)) - 50
        const newMax = Math.max(...newData.map((d) => d.y)) + 100

        // Update chart dynamically
        chartInstance.setOption({
          xAxis: { data: newData.map((d) => d.x) },
          yAxis: { min: newMin, max: newMax },
          series: [{ data: newData.map((d) => d.y) }],
        })

        return newData
      })
    }, 10) // Updates every 10ms (0.04s)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
    // eslint-disable-next-line
  }, [chartInstance, ecgData])

  return (
    <Box data-testid='ecg-box' ref={chartRef} width='100%' height='230px' />
  )
}

export default ECGChart
